import { useEffect } from "react";

interface PageMetaProps {
  title?: string;
  description: string;
  keywords?: string;
  robots?: string; // e.g., "index, follow" or "noindex, nofollow"
}

/**
 * Custom hook to manage page-specific meta tags
 * Sets page title, meta description, keywords, and robots directives for better SEO
 */
export function usePageMeta({ title, description, keywords, robots }: PageMetaProps) {
  useEffect(() => {
    // Set page title
    if (title) {
      document.title = `${title} | Cakewalk Benefits`;
    } else {
      document.title = "Cakewalk Benefits";
    }

    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    } else {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      metaDescription.setAttribute("content", description);
      document.head.appendChild(metaDescription);
    }

    // Set meta keywords if provided
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute("content", keywords);
      } else {
        metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");
        metaKeywords.setAttribute("content", keywords);
        document.head.appendChild(metaKeywords);
      }
    }

    // Set robots meta tag if provided
    if (robots) {
      let metaRobots = document.querySelector('meta[name="robots"]');
      if (metaRobots) {
        metaRobots.setAttribute("content", robots);
      } else {
        metaRobots = document.createElement("meta");
        metaRobots.setAttribute("name", "robots");
        metaRobots.setAttribute("content", robots);
        document.head.appendChild(metaRobots);
      }
    }

    // Cleanup function to reset on unmount
    return () => {
      document.title = "Cakewalk Benefits";
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute(
          "content",
          "Cakewalk Benefits - Comprehensive benefits management platform for small business owners, insurance agents, and employees",
        );
      }
    };
  }, [title, description, keywords, robots]);
}
